import type { Scale2d } from "../2d"
import type { Equipment } from "../design"

const DEFAULT_HEIGHT = 66
const DEFAULT_WIDTH = 40

const METERS_PER_DEGREE_LATITUDE = 111_111

/*
 * Lines of longitude are closer together towards the poles -
 * therefore this constant is actually a function of latitude.
 */
const METERS_PER_DEGREE_LONGITUDE = (latitude: number) =>
  METERS_PER_DEGREE_LATITUDE * Math.abs(Math.cos(latitude))

const INCHES_PER_METER = 39.3701
function inchesToMeters(inches: number): number {
  return inches / INCHES_PER_METER
}
//https://groups.google.com/g/google-maps-js-api-v3/c/hDRO4oHVSeM/m/osOYQYXg2oUJ
// const GOOGLE_METERS_PER_PIXEL_CONSTANT = 156543.03392
//https://help.nearmap.com/kb/articles/293-zoom-and-x-y-values-in-tms
// const NEARMAP_METERS_PER_PIXEL_CONSTANT = 155495.018
//https://docs.microsoft.com/en-us/bingmaps/articles/understanding-scale-and-resolution?redirectedfrom=MSDN#calculating-resolution
const BING_METERS_PER_PIXEL_CONSTANT = 156543.04

function metersPerPixel(latitude: number, zoomLevel: number) {
  const latInRadians = (latitude * Math.PI) / 180

  return (
    BING_METERS_PER_PIXEL_CONSTANT * (Math.cos(latInRadians) / 2 ** zoomLevel)
  )
}

export type MakeInchScalerOptions = {
  latitude: number
  zoomLevel: number
}

export type ScaleInchesToImageOptions = MakeInchScalerOptions & {
  inches: number
}

export const makeInchScaler = ({
  latitude,
  zoomLevel,
}: MakeInchScalerOptions): ((inches: number) => number) => {
  const mPP = metersPerPixel(latitude, zoomLevel)

  return (inches: number) => inchesToMeters(inches) / mPP
}

export const scaleInchesToImage = ({
  inches,
  latitude,
  zoomLevel,
}: ScaleInchesToImageOptions): number => {
  const mPP = metersPerPixel(latitude, zoomLevel)
  const meters = inchesToMeters(inches)

  return meters / mPP
}

// accept pixels and convert to meters and return inches
export const scalePixelsToInches = ({
  pixels,
  latitude,
  zoomLevel,
}: {
  pixels: number
  latitude: number
  zoomLevel: number
}): number => {
  const mPP = metersPerPixel(latitude, zoomLevel)
  const meters = pixels * mPP

  return meters * INCHES_PER_METER
}

export type ScaleVectorToImageOptions = {
  vector: Scale2d
  latitude: number
  longitude: number
  zoomLevel: number
}

// this isn't being used anywhere
export function scalePixelsVectorToImageLatLng({
  vector,
  dimensions,
  latitude,
  longitude,
  zoomLevel,
}: ScaleVectorToImageOptions & {
  dimensions: Scale2d
}): Scale2d {
  /*
   *  The first thing we do is convert the pixels to degrees so that we only
   *  tranform vectors in one unit. More on that conversion below.
   *
   *  Dimensional analysis for how we convert from the input pixels into degrees
   *   x pixels      n meters     n degrees latitude      x deg lat
   *  ----------- * ---------- * --------------------- = ----------
   *      1          1 pixel           1 meter               1
   *
   *   y pixels      n meters     n degrees longitude     y deg lng
   *  ----------- * ---------- * --------------------- = ----------
   *      1          1 pixel            1 meter              1
   */
  const XMpP = metersPerPixel(latitude, zoomLevel)
  const YMpP = metersPerPixel(longitude, zoomLevel)

  const x = (vector.w * XMpP) / METERS_PER_DEGREE_LONGITUDE(latitude)
  const y = (vector.h * YMpP) / METERS_PER_DEGREE_LATITUDE

  const dimx = (dimensions.w * XMpP) / METERS_PER_DEGREE_LONGITUDE(latitude)
  const dimy = (dimensions.h * YMpP) / METERS_PER_DEGREE_LATITUDE

  /*
   *    0           dimx
   *    +-------------+
   *    | x,y *       |
   *    |             |
   *    |      @      |
   *    |    lat,lon  |
   *    |             |
   *    +-------------+
   *   dimy
   *
   *  The input vector has a range of { x: [0 - dim.x], y: [0 - dim.y] }
   *
   *  Begin by normalizing the input vector to give us:
   *
   *    0             1
   *    +-------------+
   *    |    *        |
   *    |             |
   *    |      @      |
   *    |             |
   *    |             |
   *    +-------------+
   *    1
   *
   *  The normal point has a range of [0, 1]
   *
   *  We know the latlng point is in the center of the image, so its normalized
   *    vector is { x: 0.5, y: 0.5 }. We subtract the center vector from the
   *    normalized vector to get the normal distance of the input vector from the
   *    center (call this V).
   *
   *    0             1
   *    +-------------+
   *    |    *        |
   *    |     \ <- V  |
   *    |      @      |
   *    |             |
   *    |             |
   *    +-------------+
   *    1
   *
   *  The normalized distance has a range of [-0.5, 0.5].
   *
   *  Multiplying this normal distance vector by our dimension vector will scale
   *    the distance back up to degrees.
   *
   *    0            dimx
   *    +-------------+
   *    |    *        |
   *    |     \       | <- V is now scaled up
   *    |      @      |
   *    |             |
   *    |             |
   *    +-------------+
   *   dimy
   *
   *  We can then add this latlng distance vector to the origal latlng center.
   *  This gives us the latlon point of the original input vector.
   *
   *    0            dimx
   *    +-------------+
   *    |    *        | * = @ + V
   *    |     \       |
   *    |      @      |
   *    |             |
   *    |             |
   *    +-------------+
   *   dimy
   *
   */

  const normalized_distance = {
    x: x / dimx - 0.5,
    y: y / dimy - 0.5,
  }

  const latlng_distance = {
    x: normalized_distance.x * dimx,
    y: normalized_distance.y * dimy,
  }

  return { w: latitude + latlng_distance.x, h: longitude + latlng_distance.y }
}

export function scaleInchesVectorToImageMeters({
  vector,
  latitude,
  zoomLevel,
}: Omit<ScaleVectorToImageOptions, "longitude">): Scale2d {
  const mPP = metersPerPixel(latitude, zoomLevel)
  const w = inchesToMeters(vector.w) / mPP
  const h = inchesToMeters(vector.h) / mPP

  return { w, h }
}

export type ScalePanelToImageOptions = {
  solarModule: Pick<Equipment, "height" | "width">
  latitude: number
  zoomLevel: number
}

export function scalePanelToImage({
  solarModule,
  latitude,
  zoomLevel,
}: ScalePanelToImageOptions): { w: number; h: number } {
  let { height, width } = solarModule //height and width are in inches

  if (!height || height === 0) {
    height = DEFAULT_HEIGHT
  }

  if (!width || width === 0) {
    width = DEFAULT_WIDTH
  }

  const result = scaleInchesVectorToImageMeters({
    vector: { w: width, h: height },
    latitude,
    zoomLevel,
  })

  return { w: result.w, h: result.h } //height and width are in pixels
}
